import { Wrapper } from '@googlemaps/react-wrapper';
import React, { useRef } from 'react';
import { trackAnalyticsEvent } from '@eventbrite/legacy-infra.site-analytics';
import { GACodes } from '@/components/_shared/constants';
import { config } from './config';
import styles from './InteractiveMap.module.scss';

export const EVENT_LOCATION_MAP_ID = 'event-location-map';

interface Props {
  location: { latitude: number; longitude: number };
  mapZoom: number;
  gMapsApiKey: string;
}

export const InteractiveMap: React.FC<Props> = ({ location, mapZoom, gMapsApiKey }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<google.maps.Map | null>(null);

  const createOrUpdateMap = () => {
    if (!containerRef.current || !window.google?.maps) {
      return;
    }
    const center = { lat: location.latitude, lng: location.longitude };
    if (!mapRef.current) {
      mapRef.current = new window.google.maps.Map(containerRef.current, {
        ...config,
        center,
        zoom: mapZoom,
      });
      new window.google.maps.Marker({ position: center, map: mapRef.current, icon: POINTER_ICON });

      mapRef.current.addListener('zoom_changed', () => {
        trackAnalyticsEvent({
          category: GACodes.GA_CATEGORY_LISTING,
          action: GACodes.GA_ACTION_MAP_INTERACTION,
          label: GACodes.GA_LABEL_ZOOM,
        });
      });
    } else {
      mapRef.current.setCenter(center);
      mapRef.current.setZoom(mapZoom);
    }
  };

  return (
    <div role="region" id={EVENT_LOCATION_MAP_ID} data-testid="event-location-map">
      <Wrapper
        apiKey={gMapsApiKey}
        render={(status) => {
          if (status === 'SUCCESS') {
            createOrUpdateMap();
          }

          return (
            <div
              ref={containerRef}
              style={{ minWidth: '340px', minHeight: '340px' }}
              aria-hidden="true"
              className={styles.interactiveMap}
            />
          );
        }}
      />
    </div>
  );
};

const POINTER_ICON =
  'data:image/svg+xml;charset=UTF-8,' +
  encodeURIComponent(
    '<svg width="31" height="44" viewBox="0 0 31 44" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M15.1775 0.666626C6.82323 0.666626 0.0664062 7.34396 0.0664062 15.6C0.0664062 26.8 15.1775 43.3333 15.1775 43.3333C15.1775 43.3333 30.2886 26.8 30.2886 15.6C30.2886 7.34396 23.5318 0.666626 15.1775 0.666626ZM15.1775 20.9333C12.1985 20.9333 9.78069 18.5439 9.78069 15.5999C9.78069 12.6559 12.1985 10.2666 15.1775 10.2666C18.1566 10.2666 20.5743 12.6559 20.5743 15.5999C20.5743 18.5439 18.1566 20.9333 15.1775 20.9333Z" fill="#3659E3"/></svg>',
  );
